// SPDX-License-Identifier: MIT
//
// Desktop-only : surfaces the running app version (Cargo.toml / tauri.conf.json
// source of truth) + checks /dl/latest.json for the latest published beta. The
// /welcome sidebar's desktop account block consumes both to render
//   "Genesis Desktop v<x.y.z>"  +  "⬆ Mise à jour v<y> disponible → /download"
// Web callers MUST gate on detectPlatform() === 'desktop' before importing or
// calling — `getAppVersion()` invokes tauriInvoke() which throws on web.
import { tauriInvoke } from './tauri';

export async function getAppVersion(): Promise<string> {
  const v = await tauriInvoke()('get_app_version');
  return typeof v === 'string' ? v : '';
}

export interface LatestVersionInfo {
  version: string;
  url: string;
  notes?: string;
}

// Reads /dl/latest.json (served by Caddy from public/dl/latest.json at build
// time). Returns null on ANY failure — network error, non-2xx, malformed body
// or missing `version` field. The desktop sidebar must not nag the user about
// an "update" we can't verify ; silent fallthrough is the correct UX.
export async function getLatestPublishedVersion(): Promise<string | null> {
  try {
    const res = await fetch('/dl/latest.json', { cache: 'no-cache' });
    if (!res.ok) return null;
    const j = await res.json() as Partial<LatestVersionInfo>;
    return typeof j.version === 'string' && j.version.length > 0 ? j.version : null;
  } catch {
    return null;
  }
}

// Permissive semver compare. Splits on '.', coerces each segment to int.
// Returns true iff `latest` is strictly newer than `current`. Missing
// segments are treated as 0 (so "2.1" == "2.1.0"). Malformed inputs
// (any non-numeric segment) → false : we never want to flag an update on
// garbage data.
export function needsUpdate(current: string, latest: string): boolean {
  if (!current || !latest) return false;
  const c = current.split('.').map((n) => parseInt(n, 10));
  const l = latest.split('.').map((n) => parseInt(n, 10));
  if (c.some((n) => Number.isNaN(n)) || l.some((n) => Number.isNaN(n))) return false;
  const len = Math.max(c.length, l.length);
  for (let i = 0; i < len; i++) {
    const a = c[i] ?? 0; const b = l[i] ?? 0;
    if (b > a) return true;
    if (b < a) return false;
  }
  return false;
}
